/* eslint-disable complexity */
import _ from 'lodash';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { Button, Spinner, Alert } from 'design-system';
import HideableSection from 'corp/components/UI/HideableSection';
import Tooltip from 'design-system/Tooltip';
import { WorkerSlackChannel } from '@scale/shared/workerSlack/types';
import { ChannelOptions } from '@scale/shared/workerSlack/constants';
import SlackChannel from 'corp/components/SlackChannel/SlackChannel';
import { ITagJson } from '@scale/shared/types/tag';
import { ICommunityResponse, ICommunityOption } from '@scale/shared/types/Community';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';

interface NewSlackChannelEditorProps {
  rawAllChannels?: $FIXME[];
  projectId?: $FIXME;
  workerTeamId?: string;
  projectName?: string;
  workerTeamName?: string;
  showTitle?: boolean;
  isEditable?: boolean;
  permissionGroup?: string;
  communityOptions?: ICommunityResponse[];
  onChannelsCreated?: (channels: $FIXME[]) => void;
}

const NewSlackChannelEditor = (props: NewSlackChannelEditorProps) => {
  const [currentChannels, setCurrentChannels] = useState<WorkerSlackChannel[]>([]);
  const [initialCurrentChannels, setInitialCurrentChannels] = useState<WorkerSlackChannel[]>([]);
  const [addChannels, setAddChannels] = useState<WorkerSlackChannel[]>([]);
  const [removeChannels, setRemoveChannels] = useState<WorkerSlackChannel[]>([]);
  const [recommendedChannels, setRecommendedChannels] = useState<WorkerSlackChannel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alertMsg] = useState('');
  const newChannelEl = useRef<HTMLDivElement>(null);
  const [rawChannels, setRawChannels] = useState<$FIXME[]>(props.rawAllChannels || []);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const mappedCommunityOptions: ICommunityOption[] =
    props?.communityOptions?.map(option => ({
      label: option.name,
      value: option._id.toString(),
    })) ?? [];

  const handleRawChannelsLoading = useCallback(async () => {
    if (!props.rawAllChannels) {
      setIsLoading(true);
      const channelsResponse = await apiFetch(null, 'corp-api/slack_channels', {
        method: 'GET',
      });
      setRawChannels(channelsResponse || []);
      setIsLoading(false);
    }
  }, [props.rawAllChannels]);

  useEffect(() => {
    handleRawChannelsLoading();
  }, [handleRawChannelsLoading]);

  const isDirty = useMemo(() => {
    return (
      addChannels.length ||
      removeChannels.length ||
      recommendedChannels?.filter(c => c.toAdd).length ||
      currentChannels?.filter((c: WorkerSlackChannel) => c.toEdit).length
    );
  }, [addChannels, removeChannels, recommendedChannels, currentChannels]);

  const handleChannelResponses = useCallback((slackChannels: WorkerSlackChannel[]) => {
    setCurrentChannels(slackChannels);
    setInitialCurrentChannels(_.cloneDeep(slackChannels));
    setRecommendedChannels([]);
  }, []);

  const getChannels = useCallback(async () => {
    try {
      setIsLoading(true);
      const slackChannels = await apiFetch(
        null,
        props.projectId
          ? `corp-api/scalers/slack_channels/channels/projects/${props.projectId}`
          : `corp-api/scalers/slack_channels/channels/worker_teams/${props.workerTeamId}`,
      );
      handleChannelResponses(slackChannels);
    } catch (err) {
      defaultApiFetchErrorHandler(err, { handleHttp: true });
    } finally {
      setIsLoading(false);
    }
  }, [handleChannelResponses, props.projectId, props.workerTeamId]);

  useEffect(() => {
    if (props.projectId || props.workerTeamId) {
      getChannels();
    }
  }, [getChannels, props.projectId, props.workerTeamId]);

  const handleSave = async () => {
    try {
      setShowSuccessAlert(false);
      setIsLoading(true);
      const recommendationChannelsToAdd = recommendedChannels.filter(
        (c: WorkerSlackChannel) => c.toAdd,
      );
      const currentChannelsToEdit = (currentChannels || [])
        .filter((c: WorkerSlackChannel) => c.toEdit)
        .map(c => {
          const newChannel: $FIXME = _.cloneDeep(c);
          if (newChannel.tags) {
            newChannel.tags = newChannel.tags.map((tag: ITagJson) => tag._id);
          }
          return newChannel;
        });
      const channelsWithNoCommunities = [...addChannels, ...currentChannelsToEdit].filter(
        channel => !channel.communities?.length,
      );

      if (channelsWithNoCommunities.length) {
        // @ts-ignore
        defaultApiFetchErrorHandler({
          message: `The channels to create should have at least one community.`,
        });
        setIsLoading(false);
        return;
      }

      const updateSlackChannelsResponse = await apiFetch(
        null,
        'corp-api/scalers/slack_channels/channels/handle_multiple',
        {
          method: 'POST',
          body: {
            projectId: props.projectId,
            workerTeamId: props.workerTeamId,
            addChannels: [...addChannels, ...recommendationChannelsToAdd].map(c => {
              const newChannel: $FIXME = _.cloneDeep(c);
              if (newChannel.tags) {
                newChannel.tags = newChannel.tags.map((tag: ITagJson) => tag._id);
              }
              return newChannel;
            }),
            editChannels: currentChannelsToEdit,
            removeChannels,
          },
        },
      );

      if (props.onChannelsCreated) {
        props.onChannelsCreated(updateSlackChannelsResponse.addedChannels);
      }
      getChannels();
      setAddChannels([]);
      setRemoveChannels([]);

      if (updateSlackChannelsResponse.errors) {
        updateSlackChannelsResponse.errors.forEach(
          ({ name, error }: { name: string; error: string }) => {
            defaultApiFetchErrorHandler(
              // @ts-ignore
              { response: error },
              {
                handleHttp: true,
                message: `${name} failed due to ${error}`,
              },
            );
          },
        );
      }

      if (updateSlackChannelsResponse?.addedChannels?.length) {
        setShowSuccessAlert(true);
      }
    } catch (err) {
      defaultApiFetchErrorHandler(err, { handleHttp: true });
      setIsLoading(false);
    }
  };

  const handleAddRecommended = (channel: WorkerSlackChannel) => {
    let newRecommendedChannels = _.cloneDeep(recommendedChannels);
    newRecommendedChannels = newRecommendedChannels.map((c: WorkerSlackChannel) =>
      c.name === channel.name ? { ...c, toAdd: true } : c,
    );

    setRecommendedChannels(newRecommendedChannels);
  };

  const handleCancelAddRecommended = (channel: WorkerSlackChannel) => {
    let newRecommendedChannels = _.cloneDeep(recommendedChannels);
    newRecommendedChannels = newRecommendedChannels.map((c: WorkerSlackChannel) =>
      c.name === channel.name ? { ...c, toAdd: false } : c,
    );

    setRecommendedChannels(newRecommendedChannels);
  };

  const handleRemoveChannel = async (channel: WorkerSlackChannel) => {
    const addChannelsWithoutGiven = addChannels.filter(
      (c: WorkerSlackChannel) => c.name !== channel.name,
    );
    const isRemoveAdded = addChannelsWithoutGiven.length !== addChannels.length;
    if (isRemoveAdded) {
      setAddChannels(addChannelsWithoutGiven);
    } else {
      const newChannels = currentChannels.map((c: WorkerSlackChannel) =>
        c.channelId === channel.channelId ? { ...c, toRemove: true, toEdit: false } : c,
      );
      setCurrentChannels(newChannels);
      setRemoveChannels([...removeChannels, channel]);
    }
  };

  const handleCancelRemoveChannel = (channel: WorkerSlackChannel) => {
    const newRemoveChannels = removeChannels.filter(
      (c: WorkerSlackChannel) => channel.name !== c.name,
    );
    setRemoveChannels(newRemoveChannels);
  };

  const processUpdatedReviewLevels = (reviewLevels: $FIXME[]) => {
    const isAnyLevel =
      reviewLevels.length === 0 || reviewLevels[reviewLevels.length - 1].value === '';

    if (isAnyLevel) {
      return [''];
    }
    return reviewLevels.map(({ value }: { value: string }) => value).filter(value => value !== '');
  };

  const handleOptionChange = (
    channel: WorkerSlackChannel,
    key: ChannelOptions,
    options: $FIXME | $FIXME[],
    originChannels: WorkerSlackChannel[],
    cb: (channels: WorkerSlackChannel[]) => void,
  ) => {
    let values =
      key === ChannelOptions.tags || key === ChannelOptions.requireAllTags
        ? options
        : options.map((option: { label: string; value: string }) => option.value);
    if (key === ChannelOptions.reviewLevels) {
      values = processUpdatedReviewLevels(options);
    }

    const newChannels = originChannels.map((c: WorkerSlackChannel) =>
      c.name === channel.name ? { ...c, [key]: values } : c,
    );
    cb(newChannels);
  };

  const handleEditCurrentChannel = (channel: WorkerSlackChannel) => {
    const newCurrentChannels = currentChannels.map((c: WorkerSlackChannel) =>
      c.name === channel.name ? { ...c, toEdit: true, toRemove: false } : c,
    );
    setCurrentChannels(newCurrentChannels);
    handleCancelRemoveChannel(channel);
  };

  const handleCancelEditCurrentChannel = (channel: WorkerSlackChannel) => {
    const channelToReset = initialCurrentChannels.filter(c => c.name === channel.name)[0];
    const newCurrentChannels = currentChannels.map((c: WorkerSlackChannel) =>
      c.name === channel.name ? channelToReset : c,
    );
    setCurrentChannels(newCurrentChannels);
  };

  const handleOverallCancel = () => {
    setAddChannels([]);
    setRemoveChannels([]);
    handleChannelResponses(initialCurrentChannels);
  };

  const showLoader = !!(
    recommendedChannels?.length ||
    currentChannels?.length ||
    addChannels?.length
  );

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && showLoader && (
        <div
          style={{
            background: 'white',
            opacity: 0.6,
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <Spinner center size="lg" />
        </div>
      )}
      {(props.projectName || props.workerTeamName) && (
        <HideableSection
          name={`${
            props.showTitle
              ? `${props.projectName ? '[Project]' : '[Worker Team]'} ${
                  props.projectName || props.workerTeamName!
                }`
              : 'Slack Channels'
          }`}
          showOverflow={true}
        >
          <div className="container">
            <div className="container-inner">
              <div className="select-container">
                {alertMsg && <Alert severity="warning">{alertMsg}</Alert>}
              </div>
              {currentChannels && !!currentChannels.length && (
                <>
                  <div className="sp__2" />
                  <h3>Current Channels</h3>
                  <div className="sp__1" />
                  {currentChannels.map((channel: WorkerSlackChannel, index: number) => {
                    try {
                      channel.memberCount = rawChannels.filter(
                        c => c.channelId === channel.channelId,
                      )[0].memberCount;
                      // eslint-disable-next-line no-empty
                    } catch (err) {}

                    return (
                      <SlackChannel
                        permissionGroup={props.permissionGroup}
                        key={index}
                        channel={channel}
                        isEditable={false}
                        communityOptions={mappedCommunityOptions}
                        // disableInviteTaskers
                        isRemove={
                          !!removeChannels.filter(
                            (removeChannel: WorkerSlackChannel) =>
                              removeChannel.name === channel.name,
                          ).length
                        }
                        isCurrent
                        isLoading={isLoading}
                        onEdit={handleEditCurrentChannel}
                        onCancelEdit={handleCancelEditCurrentChannel}
                        onRemove={handleRemoveChannel}
                        onCancelRemove={handleCancelRemoveChannel}
                        onPermissionLevelChange={(reviewLevels: $FIXME[]) =>
                          handleOptionChange(
                            channel,
                            ChannelOptions.reviewLevels,
                            reviewLevels,
                            currentChannels,
                            setCurrentChannels,
                          )
                        }
                        onPermissionCommunitiesChange={(communities: ICommunityOption[]) =>
                          handleOptionChange(
                            channel,
                            ChannelOptions.communities,
                            communities,
                            currentChannels,
                            setCurrentChannels,
                          )
                        }
                        onPermissionWorkerSourcesChange={(workerSources: $FIXME[]) =>
                          handleOptionChange(
                            channel,
                            ChannelOptions.workerSources,
                            workerSources,
                            currentChannels,
                            setCurrentChannels,
                          )
                        }
                        onPermissionCountriesChange={(countries: $FIXME[]) =>
                          handleOptionChange(
                            channel,
                            ChannelOptions.countries,
                            countries,
                            currentChannels,
                            setCurrentChannels,
                          )
                        }
                        onPermissionTierStatusesChange={(tierStatuses: $FIXME[]) =>
                          handleOptionChange(
                            channel,
                            ChannelOptions.tierStatuses,
                            tierStatuses,
                            currentChannels,
                            setCurrentChannels,
                          )
                        }
                        onPermissionTagsChange={(tags: $FIXME[]) =>
                          handleOptionChange(
                            channel,
                            ChannelOptions.tags,
                            tags,
                            currentChannels,
                            setCurrentChannels,
                          )
                        }
                        onPermissionRequireAllTagsChange={(requireAllTags: boolean) =>
                          handleOptionChange(
                            channel,
                            ChannelOptions.requireAllTags,
                            requireAllTags,
                            currentChannels,
                            setCurrentChannels,
                          )
                        }
                      />
                    );
                  })}
                </>
              )}
              <div ref={newChannelEl}>
                {addChannels && !!addChannels.length && (
                  <>
                    <div className="sp__2" />
                    <h3>New Channels</h3>
                    <div className="sp__1" />
                    {addChannels.map((channel, index) => {
                      return (
                        <SlackChannel
                          key={index}
                          channel={channel}
                          isEditable={props.isEditable}
                          communityOptions={mappedCommunityOptions}
                          isAdd
                          // disableInviteTaskers
                          isLoading={isLoading}
                          onRemove={handleRemoveChannel}
                          onPermissionLevelChange={(reviewLevels: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.reviewLevels,
                              reviewLevels,
                              addChannels,
                              setAddChannels,
                            )
                          }
                          onPermissionCommunitiesChange={(communities: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.communities,
                              communities,
                              addChannels,
                              setAddChannels,
                            )
                          }
                          onPermissionWorkerSourcesChange={(workerSources: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.workerSources,
                              workerSources,
                              addChannels,
                              setAddChannels,
                            )
                          }
                          onPermissionCountriesChange={(countries: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.countries,
                              countries,
                              addChannels,
                              setAddChannels,
                            )
                          }
                          onPermissionTierStatusesChange={(tierStatuses: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.tierStatuses,
                              tierStatuses,
                              addChannels,
                              setAddChannels,
                            )
                          }
                          onPermissionTagsChange={(tags: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.tags,
                              tags,
                              addChannels,
                              setAddChannels,
                            )
                          }
                          onPermissionRequireAllTagsChange={(requireAllTags: boolean) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.requireAllTags,
                              requireAllTags,
                              addChannels,
                              setAddChannels,
                            )
                          }
                        />
                      );
                    })}
                  </>
                )}
              </div>

              <>
                {recommendedChannels && !!recommendedChannels.length && (
                  <>
                    <div className="sp__2" />
                    <h3>Recommended Channels</h3>
                    <div className="sp__1" />
                    {recommendedChannels.map((channel: WorkerSlackChannel, index: number) => {
                      return (
                        <SlackChannel
                          key={index}
                          channel={channel}
                          isEditable={props.isEditable}
                          // disableInviteTaskers
                          isRecommended
                          isRemove={
                            !!removeChannels.filter(
                              (removeChannel: WorkerSlackChannel) =>
                                removeChannel.name === channel.name,
                            ).length
                          }
                          isLoading={isLoading}
                          onRemove={handleRemoveChannel}
                          onCancelRemove={handleCancelRemoveChannel}
                          onAddRecommended={handleAddRecommended}
                          onCancelAddRecommended={handleCancelAddRecommended}
                          onPermissionLevelChange={(reviewLevels: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.reviewLevels,
                              reviewLevels,
                              recommendedChannels,
                              setRecommendedChannels,
                            )
                          }
                          onPermissionWorkerSourcesChange={(workerSources: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.workerSources,
                              workerSources,
                              recommendedChannels,
                              setRecommendedChannels,
                            )
                          }
                          onPermissionCountriesChange={(countries: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.countries,
                              countries,
                              recommendedChannels,
                              setRecommendedChannels,
                            )
                          }
                          onPermissionTierStatusesChange={(tierStatuses: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.tierStatuses,
                              tierStatuses,
                              recommendedChannels,
                              setRecommendedChannels,
                            )
                          }
                          onPermissionTagsChange={(tags: $FIXME[]) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.tags,
                              tags,
                              recommendedChannels,
                              setRecommendedChannels,
                            )
                          }
                          onPermissionRequireAllTagsChange={(requireAllTags: boolean) =>
                            handleOptionChange(
                              channel,
                              ChannelOptions.requireAllTags,
                              requireAllTags,
                              recommendedChannels,
                              setRecommendedChannels,
                            )
                          }
                        />
                      );
                    })}
                  </>
                )}
              </>
            </div>
            <div className="sp__2" />
            <div className="action-container">
              <div className="action-container-inner">
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Tooltip
                    title={props.isEditable ? 'Save edit' : 'No permission to edit slack channels'}
                    placement="top"
                    arrow
                  >
                    <div>
                      <Button type="primary" onClick={handleSave} disabled={isLoading}>
                        Save
                      </Button>
                    </div>
                  </Tooltip>
                  <div className="hsp__1" />
                  <Button
                    type="secondary"
                    onClick={handleOverallCancel}
                    disabled={!props.isEditable || isLoading || !isDirty}
                  >
                    Cancel
                  </Button>
                </div>
                <Collapse in={showSuccessAlert}>
                  <Alert
                    severity="success"
                    action={
                      <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <Button href="https://airtable.com/shrnOKrJwtB8sIlOb" target="_blank">
                          Request approval
                        </Button>
                        <IconButton
                          aria-label="close"
                          color="inherit"
                          size="small"
                          onClick={() => setShowSuccessAlert(false)}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </div>
                    }
                  >
                    Channels saved successfully
                  </Alert>
                </Collapse>
              </div>
            </div>
          </div>
          <style jsx>{`
            .container {
              padding: 10px 24px;
            }
            .container-inner {
              display: flex;
              flex-direction: column;
            }
            .action-container {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }
            .action-container-inner {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: center;
              gap: 32px;
            }
            .select-container {
              width: 800px;
              display: flex;
              align-items: center;
            }
          `}</style>
        </HideableSection>
      )}
    </div>
  );
};

export default NewSlackChannelEditor;
